import { MIN_PASSWORD_LENGTH } from '../utils/passwordPolicy';
import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiEye, FiEyeOff, FiShield } from 'react-icons/fi';
import api from '../api';

export default function ActivateAccount() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token')?.trim() || '', [searchParams]);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!token) {
      setError('O link de ativação está incompleto. Solicite um novo convite.');
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`A senha deve conter pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`);
      return;
    }
    if (password !== confirmation) {
      setError('As senhas digitadas não coincidem.');
      return;
    }

    setLoading(true);
    try {
      await api.post(
        '/auth/activate',
        { token, password },
        { skipAuthHeader: true, skipAuthRefresh: true, withCredentials: false },
      );
      setPassword('');
      setConfirmation('');
      setSuccess(true);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error ||
          'Não foi possível ativar a conta. O link pode ter expirado ou já ter sido utilizado.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 px-4 py-10">
      <section className="w-full max-w-md rounded-3xl border border-white/10 bg-white p-7 shadow-2xl">
        <div className="mb-6 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 text-3xl text-blue-700">
            <FiShield aria-hidden />
          </div>
          <h1 className="mt-4 text-2xl font-semibold text-slate-900">Ativar conta</h1>
          <p className="mt-2 text-sm text-slate-600">
            Crie sua senha pessoal. O link é temporário e só pode ser utilizado uma vez.
          </p>
        </div>

        {success ? (
          <div className="space-y-5 text-center">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-5 text-emerald-800">
              <p className="font-semibold">Conta ativada com sucesso.</p>
              <p className="mt-1 text-sm">Entre com o e-mail informado no convite e a senha que você acabou de criar.</p>
            </div>
            <Link
              to="/"
              className="inline-flex w-full items-center justify-center rounded-xl bg-blue-700 px-4 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow hover:bg-blue-600"
            >
              Ir para o login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-center text-sm font-medium text-red-700">
                {error}
              </p>
            )}
            <div>
              <label htmlFor="activation-password" className="mb-1 block text-sm font-semibold text-slate-700">
                Nova senha
              </label>
              <div className="relative">
                <input
                  id="activation-password"
                  name="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  minLength={MIN_PASSWORD_LENGTH}
                  maxLength={72}
                  autoComplete="new-password"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 pr-12 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400/40"
                  required
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-slate-500 hover:text-blue-700"
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? <FiEyeOff aria-hidden /> : <FiEye aria-hidden />}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">Use pelo menos {MIN_PASSWORD_LENGTH} caracteres.</p>
            </div>
            <div>
              <label htmlFor="activation-password-confirmation" className="mb-1 block text-sm font-semibold text-slate-700">
                Confirmar nova senha
              </label>
              <input
                id="activation-password-confirmation"
                name="new-password-confirmation"
                type={showPassword ? 'text' : 'password'}
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                minLength={MIN_PASSWORD_LENGTH}
                maxLength={72}
                autoComplete="new-password"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400/40"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading || !token}
              className="w-full rounded-xl bg-gradient-to-r from-blue-700 to-blue-400 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Ativando...' : 'Ativar conta'}
            </button>
            {!token && (
              <p className="text-center text-xs text-slate-500">
                Abra o endereço completo recebido por e-mail.
              </p>
            )}
          </form>
        )}
      </section>
    </main>
  );
}
